/**
 * Modal Global de Redes Sociales — RedesSocialesModal
 * Componente transversal y reutilizable para cualquier módulo de AviFácil.
 * Muestra los canales y redes oficiales (Instagram, Facebook, YouTube, Google, TikTok).
 */

'use client';

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import {
  Share2,
  X,
  ExternalLink,
  Copy,
  Check,
  BadgeCheck,
} from 'lucide-react';
import styles from './RedesSocialesModal.module.css';

export type RedSocialId = 'TODAS' | 'INSTAGRAM' | 'FACEBOOK' | 'YOUTUBE' | 'GOOGLE' | 'TIKTOK' | 'WHATSAPP';

export interface RedSocialInfo {
  id: RedSocialId;
  nombre: string;
  handle: string;
  descripcion: string;
  url: string;
  colorBg: string;
  icon: React.ReactNode;
}

export const CANALES_OFICIALES: RedSocialInfo[] = [
  {
    id: 'INSTAGRAM',
    nombre: 'Instagram',
    handle: '@avifacil.co',
    descripcion: 'Novedades diarias, historias de campo, consejos de Elie y comunidad avícola activa.',
    url: 'https://instagram.com',
    colorBg: 'linear-gradient(45deg, #F58529, #DD2A7B, #8134AF, #515BD4)',
    icon: (
      <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
      </svg>
    ),
  },
  {
    id: 'FACEBOOK',
    nombre: 'Facebook',
    handle: 'AviFácil Latam',
    descripcion: 'Eventos en vivo, grupos de avicultores, guías de bioseguridad y noticias del sector.',
    url: 'https://facebook.com',
    colorBg: '#1877F2',
    icon: (
      <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
  },
  {
    id: 'YOUTUBE',
    nombre: 'YouTube',
    handle: 'AviFácil TV',
    descripcion: 'Tutoriales paso a paso de censos, curvas zootécnicas, pesajes y configuración de galpones.',
    url: 'https://youtube.com',
    colorBg: '#FF0000',
    icon: (
      <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  },
  {
    id: 'TIKTOK',
    nombre: 'TikTok',
    handle: '@avifacil_tips',
    descripcion: 'Tips rápidos de 30 segundos sobre manejo de camadas, tolvas automáticas y control de clima.',
    url: 'https://tiktok.com',
    colorBg: '#000000',
    icon: (
      <svg className="w-4.5 h-4.5 fill-current" viewBox="0 0 24 24">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.24 1.07-.14 1.61.24 1.64 1.82 2.89 3.5 2.77 1.45-.04 2.76-.98 3.19-2.36.14-.48.2-1 .2-1.51.01-5.77.01-11.53.01-17.3z" />
      </svg>
    ),
  },
  {
    id: 'GOOGLE',
    nombre: 'Google & Soporte',
    handle: 'AviFácil en Google',
    descripcion: 'Puntajes, reseñas oficiales, documentación y centro de descargas para productores.',
    url: 'https://google.com',
    colorBg: '#4285F4',
    icon: (
      <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
        <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.344-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" />
      </svg>
    ),
  },
];

export interface RedesSocialesModalProps {
  isOpen: boolean;
  onClose: () => void;
  redInicial?: RedSocialId;
}

export function RedesSocialesModal({
  isOpen,
  onClose,
  redInicial = 'TODAS',
}: RedesSocialesModalProps) {
  const [mounted, setMounted] = useState(false);
  const [activeTab, setActiveTab] = useState<RedSocialId>(redInicial);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    setActiveTab(redInicial);
  }, [redInicial]);

  // Cerrar con Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleCopyLink = (item: RedSocialInfo) => {
    navigator.clipboard.writeText(item.url);
    setCopiedId(item.id);
    toast.success(`Enlace de ${item.nombre} copiado al portapapeles`);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (!isOpen || !mounted) return null;

  const redesFiltradas =
    activeTab === 'TODAS'
      ? CANALES_OFICIALES
      : CANALES_OFICIALES.filter((r) => r.id === activeTab);

  return createPortal(
    <div
      className={styles.backdrop}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="social-modal-title"
    >
      <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        {/* Encabezado */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.iconBadge}>
              <Share2 size={22} />
            </div>
            <div>
              <h2 id="social-modal-title" className={styles.title}>
                Canales Oficiales AviFácil
              </h2>
              <p className={styles.subtitle}>
                Únete a la mayor red de avicultores y productores de la región.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={styles.closeBtn}
            aria-label="Cerrar modal"
          >
            <X size={18} />
          </button>
        </div>

        {/* Barra de Filtros / Tabs */}
        <div className={styles.tabsBar}>
          <button
            type="button"
            onClick={() => setActiveTab('TODAS')}
            className={`${styles.tabBtn} ${activeTab === 'TODAS' ? styles.tabBtnActive : ''}`}
          >
            Todas ({CANALES_OFICIALES.length})
          </button>
          {CANALES_OFICIALES.map((canal) => (
            <button
              key={canal.id}
              type="button"
              onClick={() => setActiveTab(canal.id)}
              className={`${styles.tabBtn} ${activeTab === canal.id ? styles.tabBtnActive : ''}`}
            >
              {canal.nombre}
            </button>
          ))}
        </div>

        {/* Lista de Tarjetas de Redes */}
        <div className={styles.socialList}>
          {redesFiltradas.map((canal) => (
            <div key={canal.id} className={styles.socialCard}>
              <div className={styles.cardLeft}>
                <div
                  className={styles.networkIcon}
                  style={{ background: canal.colorBg }}
                >
                  {canal.icon}
                </div>
                <div className={styles.cardInfo}>
                  <div className={styles.networkName}>
                    <span>{canal.nombre}</span>
                    <BadgeCheck size={16} className="text-[#c25ee8]" />
                  </div>
                  <span className={styles.networkHandle}>{canal.handle}</span>
                  <p className={styles.networkDesc}>{canal.descripcion}</p>
                </div>
              </div>

              <div className={styles.cardActions}>
                <button
                  type="button"
                  onClick={() => handleCopyLink(canal)}
                  className={styles.copyBtn}
                  aria-label={`Copiar enlace de ${canal.nombre}`}
                  title="Copiar enlace"
                >
                  {copiedId === canal.id ? (
                    <Check size={16} className="text-green-600" />
                  ) : (
                    <Copy size={16} />
                  )}
                </button>
                <a
                  href={canal.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.visitBtn}
                >
                  <span>Seguir</span>
                  <ExternalLink size={13} />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}
